import React, { useEffect, useRef, useState } from "react";
import { formatPrice } from "../lib/format";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, X, ChevronDown } from "lucide-react";
import { LOGO_URL, api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Sheet, SheetContent } from "./ui/sheet";

export default function Header() {
  const { count, setOpen, freeShippingThreshold, shippingPromo } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [expanded, setExpanded] = useState({}); // slug → bool

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const searchInputRef = useRef(null);

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);

  // Lazy-load full product list on first search open
  useEffect(() => {
    if (searchOpen && allProducts.length === 0) {
      api.get("/products?limit=200").then(r => setAllProducts(r.data)).catch(() => {});
    }
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen, allProducts.length]);

  // Escape closes search
  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const close = () => setMenuOpen(false);
  const toggleExp = (s) => setExpanded(o => ({ ...o, [s]: !o[s] }));

  const q = query.trim().toLowerCase();
  const results = q.length < 2
    ? []
    : allProducts.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.code || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      ).slice(0, 8);

  const goToProduct = (id) => {
    setSearchOpen(false);
    setQuery("");
    navigate(`/produit/${id}`);
  };

  return (
    <>
      <header className="bg-white border-b border-berulia-border">
        {/* Whisper bar */}
        <div className="bg-berulia-rose text-berulia-inkdeep text-[11px] tracking-[0.25em] uppercase text-center py-2 border-b border-berulia-rosedeep/20">
          {shippingPromo
            ? "Ce week-end, livraison offerte dès le premier euro"
            : `Livraison offerte dès ${freeShippingThreshold} € en France métropolitaine`}
        </div>

        <div className="max-w-[1500px] mx-auto px-4 md:px-12 py-4 md:py-6 grid grid-cols-[auto_1fr_auto] items-center gap-3 md:gap-4 overflow-hidden">
          {/* Left — hamburger menu */}
          <div className="flex items-center gap-3 justify-self-start">
            <button onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="menu-btn"
              className="flex flex-col gap-[5px] py-2 px-1 group">
              <span className="block w-7 h-px bg-berulia-inkdeep transition group-hover:bg-berulia-rosedeep"/>
              <span className="block w-5 h-px bg-berulia-inkdeep transition group-hover:bg-berulia-rosedeep group-hover:w-7"/>
              <span className="block w-7 h-px bg-berulia-inkdeep transition group-hover:bg-berulia-rosedeep"/>
            </button>
            <span className="hidden md:inline overline text-berulia-inkmuted">Menu</span>
          </div>

          {/* Center — original logo as title, fluid width, never crops */}
          <Link to="/" data-testid="logo-home" className="flex justify-center select-none group min-w-0">
            <img src={LOGO_URL} alt="BERULIA — prêt-à-porter féminin"
              style={{ mixBlendMode: "multiply", objectFit: "contain", objectPosition: "center" }}
              className="w-full max-w-[420px] md:max-w-[640px] lg:max-w-[820px] h-[120px] md:h-[200px] lg:h-[240px] transition group-hover:scale-[1.02]"/>
          </Link>

          {/* Right actions */}
          <div className="flex items-center justify-end gap-5">
            <button onClick={() => setSearchOpen(true)} className="hidden md:block text-berulia-inkdeep/70 hover:text-berulia-rosedeep" aria-label="Recherche" data-testid="search-btn"><Search size={20}/></button>
            <button onClick={() => navigate(user ? "/mon-compte" : "/connexion")} data-testid="account-btn" className="text-berulia-inkdeep/70 hover:text-berulia-rosedeep"><User size={20}/></button>
            <button onClick={() => setOpen(true)} data-testid="cart-btn" className="relative text-berulia-inkdeep/70 hover:text-berulia-rosedeep">
              <ShoppingBag size={20}/>
              {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-3 bg-berulia-rosedeep text-berulia-inkdeep text-[10px] font-semibold rounded-full h-4 w-4 flex items-center justify-center">{count}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-50 bg-berulia-inkdeep/40 backdrop-blur-sm"
          onClick={() => setSearchOpen(false)}
          data-testid="search-overlay"
        >
          <div
            className="bg-white border-b border-berulia-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-3xl mx-auto px-6 py-8">
              <div className="flex items-center gap-3 border-b border-berulia-rosedeep/40 pb-3">
                <Search size={20} className="text-berulia-inkmuted" />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher une pièce, une couleur, un code…"
                  className="flex-1 bg-transparent text-lg outline-none placeholder:text-berulia-inkmuted/60"
                  data-testid="search-input"
                />
                <button onClick={() => setSearchOpen(false)} aria-label="Fermer" className="text-berulia-inkmuted hover:text-berulia-inkdeep">
                  <X size={20} />
                </button>
              </div>

              <div className="mt-6 min-h-[80px]">
                {q.length < 2 && (
                  <p className="text-sm text-berulia-inkmuted">Tapez au moins 2 lettres pour rechercher.</p>
                )}
                {q.length >= 2 && results.length === 0 && (
                  <p className="text-sm text-berulia-inkmuted">Aucun résultat pour "<strong>{query}</strong>".</p>
                )}
                {results.length > 0 && (
                  <ul className="divide-y divide-berulia-border" data-testid="search-results">
                    {results.map((p) => (
                      <li key={p.id}>
                        <button
                          onClick={() => goToProduct(p.id)}
                          className="w-full flex items-center gap-4 py-3 text-left hover:bg-berulia-cream/60 px-2 transition"
                        >
                          <img src={p.images?.[0]} alt="" className="w-12 h-14 object-cover bg-berulia-beigewarm" onError={(e) => { e.target.style.visibility = "hidden"; }} />
                          <div className="flex-1">
                            <div className="font-heading text-lg text-berulia-inkdeep">{p.name}</div>
                            <div className="overline text-berulia-inkmuted">{formatPrice(p.price)}</div>
                          </div>
                          <span className="overline text-berulia-rosedeep">Voir →</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu side drawer */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-full sm:max-w-sm bg-berulia-cream border-r border-berulia-border p-0 flex flex-col" data-testid="menu-drawer">
          <div className="px-8 pt-8 pb-6 border-b border-berulia-border flex items-center justify-between">
            <p className="overline text-berulia-rosedeep">Navigation</p>
            <button onClick={close} aria-label="Fermer" className="text-berulia-inkmuted hover:text-berulia-inkdeep"><X size={20}/></button>
          </div>

          <nav className="flex-1 overflow-y-auto px-8 py-8 space-y-1">
            <Link to="/" onClick={close} data-testid="menu-home" className="block font-heading text-3xl text-berulia-inkdeep hover:text-berulia-rosedeep py-2 transition">Accueil</Link>
            <Link to="/categorie/nouveautes" onClick={close} data-testid="menu-new" className="block font-heading text-3xl text-berulia-inkdeep hover:text-berulia-rosedeep py-2 transition">Nouveautés</Link>

            {/* All top-level categories (accordion when has children) */}
            {cats.filter(c => !c.parent_slug && c.slug !== "nouveautes" && c.slug !== "soldes").sort((a,b)=>a.sort_order-b.sort_order).map(parent => {
              const children = cats.filter(c => c.parent_slug === parent.slug).sort((a,b)=>a.sort_order-b.sort_order);
              const hasChildren = children.length > 0;
              const isOpen = !!expanded[parent.slug];
              return (
                <div key={parent.id} className={hasChildren ? "pt-2" : ""}>
                  {hasChildren ? (
                    <button onClick={() => toggleExp(parent.slug)}
                      data-testid={`menu-toggle-${parent.slug}`}
                      className="w-full flex items-center justify-between font-heading text-3xl text-berulia-inkdeep hover:text-berulia-rosedeep py-2 transition">
                      <span>{parent.name}</span>
                      <ChevronDown size={20} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-berulia-rosedeep" : ""}`}/>
                    </button>
                  ) : (
                    <Link to={`/categorie/${parent.slug}`} onClick={close}
                      data-testid={`menu-cat-${parent.slug}`}
                      className="block font-heading text-3xl text-berulia-inkdeep hover:text-berulia-rosedeep py-2 transition">
                      {parent.name}
                    </Link>
                  )}
                  {hasChildren && (
                    <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <ul className="overflow-hidden pl-4 border-l border-berulia-rose/40 mt-1 space-y-0.5">
                        <li>
                          <Link to={`/categorie/${parent.slug}`} onClick={close}
                            className="block font-serif-italic text-xl text-berulia-rosedeep hover:text-berulia-inkdeep py-1 transition">
                            Tout {parent.name.toLowerCase()}
                          </Link>
                        </li>
                        {children.map(sub => (
                          <li key={sub.id}>
                            <Link to={`/categorie/${sub.slug}`} onClick={close}
                              data-testid={`menu-cat-${sub.slug}`}
                              className="block font-serif-italic text-xl text-berulia-inkmuted hover:text-berulia-rosedeep py-1 transition">
                              {sub.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-6 border-t border-berulia-border mt-4">
              <Link to={user ? "/mon-compte" : "/connexion"} onClick={close} className="block overline text-berulia-inkmuted hover:text-berulia-rosedeep py-2">{user ? "Mon compte" : "Connexion"}</Link>
            </div>
          </nav>

          <div className="border-t border-berulia-border px-8 py-5 bg-berulia-ivory">
            <p className="overline text-berulia-inkmuted">Une question ?</p>
            <a href="mailto:contact@berulia.fr" className="font-serif-italic text-berulia-rosedeep">contact@berulia.fr</a>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
